import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Check, Clock, Hammer, Truck, Gift } from "lucide-react";
import { terrariumStyles, type PreOrder } from "@shared/schema";
import { stripHtml } from "@/lib/sanitize";

interface OrderStatusProps {
  order: PreOrder;
}

const statusSteps = [
  { id: "pending", label: "Pending", icon: Clock },
  { id: "approved", label: "Approved", icon: Check },
  { id: "building", label: "Building", icon: Hammer },
  { id: "delivered", label: "Delivered", icon: Truck },
];

function getStatusIndex(status: string | null): number {
  if (!status) return 0;
  const index = statusSteps.findIndex(s => s.id === status.toLowerCase());
  return index >= 0 ? index : 0;
}

function getStatusBadgeVariant(status: string | null): "default" | "secondary" | "outline" {
  switch (status?.toLowerCase()) {
    case "delivered":
      return "default";
    case "building":
      return "secondary";
    default:
      return "outline";
  }
}

function formatDate(date: Date | string | null): string {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function formatDeliveryWeek(weekStart: string): string {
  const start = new Date(weekStart);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);

  const formatDate = (date: Date) =>
    date.toLocaleDateString("en-US", { month: "short", day: "numeric" });

  return `Week of ${formatDate(start)} - ${formatDate(end)}`;
}

export function OrderStatus({ order }: OrderStatusProps) {
  const currentStatusIndex = getStatusIndex(order.status);
  const style = terrariumStyles.find(s => s.id === order.terrariumStyle);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="font-serif text-xl">Order Status</CardTitle>
          <Badge variant={getStatusBadgeVariant(order.status)}>
            {order.status || "Pending"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Status Tracker */}
        <div className="relative">
          <div className="flex justify-between">
            {statusSteps.map((step, index) => {
              const isCompleted = index <= currentStatusIndex;
              const isCurrent = index === currentStatusIndex;
              const StepIcon = step.icon;

              return (
                <div key={step.id} className="flex flex-col items-center relative z-10">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
                      isCompleted
                        ? "bg-primary border-primary text-primary-foreground"
                        : "bg-background border-muted-foreground/30 text-muted-foreground"
                    } ${isCurrent ? "ring-2 ring-primary ring-offset-2" : ""}`}
                  >
                    <StepIcon className="w-5 h-5" />
                  </div>
                  <span
                    className={`mt-2 text-xs font-medium ${
                      isCompleted ? "text-foreground" : "text-muted-foreground"
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
          {/* Progress Line */}
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-muted-foreground/30 -z-0">
            <div
              className="h-full bg-primary transition-all duration-500"
              style={{
                width: `${(currentStatusIndex / (statusSteps.length - 1)) * 100}%`,
              }}
            />
          </div>
        </div>

        <Separator />

        {/* Order Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h4 className="text-sm font-medium text-muted-foreground">Terrarium Style</h4>
            <p className="text-lg font-medium">{style?.name || order.terrariumStyle}</p>
            {style && <p className="text-sm text-muted-foreground">{style.description}</p>}
          </div>
          <div>
            <h4 className="text-sm font-medium text-muted-foreground">Delivery Week</h4>
            <p className="text-lg font-medium">{formatDeliveryWeek(order.deliveryWeek)}</p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-muted-foreground">Order Date</h4>
            <p className="text-lg font-medium">{formatDate(order.createdAt)}</p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-muted-foreground">Total Amount</h4>
            <p className="text-lg font-medium text-primary">
              ${order.totalAmount ? order.totalAmount.toLocaleString() : "N/A"}
            </p>
          </div>
        </div>

        {/* Special Requests */}
        {order.specialRequests && (
          <>
            <Separator />
            <div>
              <h4 className="text-sm font-medium text-muted-foreground mb-2">Special Requests</h4>
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm">{stripHtml(order.specialRequests)}</p>
              </div>
            </div>
          </>
        )}

        {/* Gift Information */}
        {order.isGift && (
          <>
            <Separator />
            <div className="bg-primary/5 border border-primary/20 p-4 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Gift className="w-5 h-5 text-primary" />
                <h4 className="font-medium">Gift Order</h4>
              </div>
              {order.giftRecipientName && (
                <div className="mb-2">
                  <span className="text-sm text-muted-foreground">Recipient: </span>
                  <span className="text-sm font-medium">{stripHtml(order.giftRecipientName)}</span>
                </div>
              )}
              {order.giftRecipientEmail && (
                <div className="mb-2">
                  <span className="text-sm text-muted-foreground">Recipient Email: </span>
                  <span className="text-sm font-medium">{stripHtml(order.giftRecipientEmail)}</span>
                </div>
              )}
              {order.giftMessage && (
                <div className="mt-3">
                  <span className="text-sm text-muted-foreground">Gift Message: </span>
                  <p className="text-sm mt-1 italic">"{stripHtml(order.giftMessage)}"</p>
                </div>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
